import React, { useState, useEffect } from 'react';
import InputMask from 'react-input-mask';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  VStack,
  Heading,
  FormErrorMessage,
  Select,
} from '@chakra-ui/react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

function CustomerInfoStep({ formData, onNext }) {
  const [stepData, setStepData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailAddress: '',
    role: '',
    salesperson: '',
    country: 'US',
  });
  const [errors, setErrors] = useState({});

  const roleOptions = [
    { value: 'homeowner', label: 'Homeowner' },
    { value: 'builder', label: 'Builder' },
    { value: 'architect', label: 'Architect' },
    { value: 'property_manager', label: 'Property Manager' },
    { value: 'distributor', label: 'Distributor' },
    { value: 'realtor', label: 'Realtor' },
    { value: 'interior_designer', label: 'Interior Designer' },
    { value: 'other', label: 'Other/Not Listed Here' },
  ];

  const salespersonOptions = [
    { value: 'craigj', label: 'Craig J' },
    { value: 'pauls', label: 'Paul S' },
    { value: 'viviank', label: 'Vivian K' },
    { value: 'kyleb', label: 'Kyle B' },
    { value: 'enzov', label: 'Enzo V' },
    { value: 'johnn', label: 'John N' },
    { value: 'other', label: 'Other/Not Sure' },
  ];


useEffect(() => {
  if (formData && Object.keys(formData).length > 0) {
    setStepData(formData);
  }
}, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setStepData({ ...stepData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!stepData.salesperson) newErrors.salesperson = 'Salesperson is required';
    if (!stepData.firstName) newErrors.firstName = 'First Name is required';
    if (!stepData.lastName) newErrors.lastName = 'Last Name is required';
    if (!stepData.phoneNumber) {
      newErrors.phoneNumber = 'Phone Number is required';
    } else {
      const phoneNumber = parsePhoneNumberFromString(stepData.phoneNumber, stepData.country || 'US');
      if (!phoneNumber || !phoneNumber.isValid()) {
        newErrors.phoneNumber = 'Invalid Phone Number';
      }
    }
    if (!stepData.emailAddress) {
      newErrors.emailAddress = 'Email Address is required';
    } else if (!/\S+@\S+\.\S+/.test(stepData.emailAddress)) {
      newErrors.emailAddress = 'Email Address is invalid';
    }
    if (!stepData.role) newErrors.role = 'Role is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextClick = () => {
    if (validate()) {
      onNext(stepData);
    }
  };

  return (
    <Box>
      <Heading as="h2" size="lg" mb={6}>
        Customer Information
      </Heading>
      <VStack spacing={4}>
        {/* Country Selector */}
        <FormControl id="country" isRequired>
          <FormLabel>Country</FormLabel>
          <Select
            name="country"
            value={stepData.country}
            onChange={handleChange}
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="MX">Mexico</option>
            <option value="GB">United Kingdom</option>
            <option value="AU">Australia</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="IN">India</option>
            <option value="JP">Japan</option>
            <option value="BR">Brazil</option>
            <option value="OT">Other</option>
          </Select>
        </FormControl>
        {/* Salesperson Dropdown */}
        <FormControl id="salesperson" isRequired isInvalid={!!errors.salesperson}>
          <FormLabel>Salesperson</FormLabel>
          <Select
            name="salesperson"
            value={stepData.salesperson || ''}
            onChange={handleChange}
            placeholder="Select your Salesperson"
          >
            {salespersonOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <FormErrorMessage>{errors.salesperson}</FormErrorMessage>
        </FormControl>
        <FormControl id="firstName" isRequired isInvalid={!!errors.firstName}>
          <FormLabel>First Name</FormLabel>
          <Input
            name="firstName"
            value={stepData.firstName || ''}
            onChange={handleChange}
          />
          <FormErrorMessage>{errors.firstName}</FormErrorMessage>
        </FormControl>

        <FormControl id="lastName" isRequired isInvalid={!!errors.lastName}>
          <FormLabel>Last Name</FormLabel>
          <Input
            name="lastName"
            value={stepData.lastName || ''}
            onChange={handleChange}
          />
          <FormErrorMessage>{errors.lastName}</FormErrorMessage>
        </FormControl>

        <FormControl id="phoneNumber" isRequired isInvalid={!!errors.phoneNumber}>
          <FormLabel>Phone Number</FormLabel>
          {stepData.country === 'US' ? (
            <InputMask
              mask="(999) 999-9999"
              maskChar=""
              value={stepData.phoneNumber}
              onChange={(e) => {
                const input = e.target.value;
                const digitsOnly = input.replace(/\D/g, '');
                setStepData({ ...stepData, phoneNumber: digitsOnly });
                if (errors.phoneNumber) {
                  setErrors({ ...errors, phoneNumber: null });
                }
              }}
            >
              {(inputProps) => (
                <Input
                  {...inputProps}
                  name="phoneNumber"
                  type="tel"
                />
              )}
            </InputMask>
          ) : (
            <Input
              name="phoneNumber"
              value={stepData.phoneNumber || ''}
              onChange={(e) => {
                const inputValue = e.target.value.replace(/[^\d+]/g, '');
                const phoneNumber = parsePhoneNumberFromString(inputValue, stepData.country);
                const formatted = phoneNumber && phoneNumber.isValid() ? phoneNumber.formatInternational() : inputValue;
                setStepData({ ...stepData, phoneNumber: formatted });
                if (errors.phoneNumber) {
                  setErrors({ ...errors, phoneNumber: null });
                }
              }}
              type="tel"
            />
          )}
          <FormErrorMessage>{errors.phoneNumber}</FormErrorMessage>
        </FormControl>

        <FormControl id="emailAddress" isRequired isInvalid={!!errors.emailAddress}>
          <FormLabel>Email Address</FormLabel>
          <Input
            name="emailAddress"
            value={stepData.emailAddress || ''}
            onChange={handleChange}
            type="email"
          />
          <FormErrorMessage>{errors.emailAddress}</FormErrorMessage>
        </FormControl>

        <FormControl id="role" isRequired isInvalid={!!errors.role}>
          <FormLabel>Role</FormLabel>
          <Select
            name="role"
            value={stepData.role || ''}
            onChange={handleChange}
            placeholder="Select your Role"
          >
            {roleOptions.map(option => (
              option.value !== '' && (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              )
            ))}
          </Select>
          <FormErrorMessage>{errors.role}</FormErrorMessage>
        </FormControl>

        <Button colorScheme="brand" onClick={handleNextClick} alignSelf="flex-end">
          Next
        </Button>
      </VStack>
    </Box>
  );
}

export default CustomerInfoStep;
